import { useState } from 'react'
import { useForm, Controller } from 'react-hook-form'
import { useLocation, useNavigate, useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { nextPage } from 'components/RCPro/helpers/nav'
import api from 'helpers/api'
import classNames from 'classnames'
import MaskedInput from 'react-text-mask'
import Input from 'components/common/Input/Input'
import InputError from 'components/common/InputError/InputError'
import Arrow from 'components/assets/icons/Arrow'
import Button from 'components/common/Button/Button'
import Spinner from 'components/common/Spinner/Spinner'
import ResponseMessage from 'components/common/ResponseMessage/ResponseMessage'
import { expirationDateValidationFx, numberValidationCardNumberFx, numberValidationCVCFx, formParams } from 'constants/inputValidation'
import style from '../Payment.module.scss'

const CardForm = ({ state }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const { nextPageTransition } = useOutletContext()
  const { CB, layout } = state.texts
  const [ errorMessage, setErrorMessage ] = useState(false)
  const [ isSpinnerLoading, setIsSpinnerLoading ] = useState(false)

  // Initialize form
  const required = t('mainReporting:form.mandatory')
  const {
    register,
    formState: { errors, isValid },
    handleSubmit,
    control
  } = useForm({
    ...formParams,
    values:{
      owner: '',
      number: '',
      expirationDate: '',
      ccv: ''
    }
  })

  // Submit form
  const onSubmit = handleSubmit(async data => {
    try {
      setErrorMessage(false)
      setIsSpinnerLoading(true)
      const [expMonth, expYear] = data.expirationDate.split('/')
      const paymentPlan = state.annual ? 'year' : 'month'

      const res = await api.post({
        url: '/adhesions/client',
        data: {
          contractType: state.contractType,
          email: state.email,
          birthdate: '20/01/2000',
          transactionHorodate: new Date(),
          firstPaymentDate: state.activation,
          paymentType: 'credit card',
          paymentPlan,
          cardOwnerFullName: data.owner,
          cardNumber: data.number.replace(/\D/g, ''),
          cvc: data.ccv.replace(/\D/g, ''),
          cardExpMonth: expMonth,
          cardExpYear: expYear,
          areTermsAccepted: state.areTermsAccepted,
          adhesionStatus: 'pending',
          frequency: {
            paymentInterval: paymentPlan
          },
        },
        headers: { Authorization: `Bearer ${state.token}` },
      })

      if (res?.response && res?.response.compliance) {
        nextPage({ pathname, state, navigate, data:{ saveID: res.response.id } })
        nextPageTransition()
      } else {
        setErrorMessage(true)
      }
    } catch (error) {
      console.log({error})
      setErrorMessage(true)
    } finally {
      setIsSpinnerLoading(false)
    }
  })


  return (
    <>
      {isSpinnerLoading && <Spinner />}

      <h1>{CB?.title}</h1>

      {errorMessage && <ResponseMessage className={style.response} label={t('common:form.genericError')} />}

      <form onSubmit={onSubmit}>

        <Input
          error={errors.owner}
          register={register('owner', { required })}
          label={t('mainReporting:form.CB.name.label')}
          placeholder={t('mainReporting:form.CB.name.placeholder')}
          disallowNumbers={true}
        />
        <InputError error={errors.owner} />


        <div className="input-label">{t('mainReporting:form.CB.number.label')}</div>
        <Controller
          control={control}
          name="number"
          rules={numberValidationCardNumberFx(t)}
          render={({ field: { onBlur, onChange, value } }) => (
            <MaskedInput
              className={classNames('masked-input', errors.number && 'error')}
              mask={[/\d/, /\d/, /\d/, /\d/,
                ' ', /\d/, /\d/, /\d/, /\d/,
                ' ', /\d/, /\d/, /\d/, /\d/,
                ' ', /\d/, /\d/, /\d/, /\d/,
              ]}
              guide={false}
              keepCharPositions={false}
              onBlur={onBlur}
              onChange={value => onChange(value)}
              placeholder={t('mainReporting:form.CB.number.placeholder')}
              value={value}
              disallowletters={value}
            />
          )}
        />
        <InputError error={errors.number} />


        <div className="input-label">{t('mainReporting:form.CB.expiration.label')}</div>
        <Controller
          control={control}
          name="expirationDate"
          rules={expirationDateValidationFx(t)}
          render={({ field: { onBlur, onChange, value } }) => (
            <MaskedInput
              className={classNames('masked-input', errors.expirationDate && 'error')}
              mask={[/\d/, /\d/, '/', /\d/, /\d/]}
              guide={false}
              keepCharPositions={false}
              onBlur={onBlur}
              onChange={value => onChange(value)}
              placeholder={t('mainReporting:form.CB.expiration.label')}
              value={value}
              disallowletters={value}
            />
          )}
        />
        <InputError error={errors.expirationDate} />


        <Input
          maxLength="4"
          error={errors.ccv}
          register={register('ccv', numberValidationCVCFx(t))}
          label={t('mainReporting:form.CB.ccv.placeholder')}
          placeholder={t('mainReporting:form.CB.ccv.placeholder')}
          disallowLetters={true}
        />
        <InputError error={errors.ccv} />


        <Button
          label={<>{layout?.next} <Arrow /></>}
          onClick={handleSubmit}
          type="primary"
          disabled={!isValid}
        />
      </form>
    </>
  )
}

export default CardForm
